import type { PdpPromotion } from '../shopware/pdpData';
import type { PromoMetadata, PromoState } from './usePromoCode';

/**
 * Bridge a /pdp-data promotion (Stage 2, ORT-3060) into the Stage-1 PromoMetadata /
 * PromoState shapes, so the existing PromoBanner text interpretation and the offer UI
 * share one source of truth. The promotion already comes scoped to the current product
 * (it is in the product's /pdp-data promotions[]), so eligibility is "this product".
 */
export function pdpPromotionToMetadata(promo: PdpPromotion): PromoMetadata {
  const discount = promo.discounts[0] ?? null;
  const type = discount?.type;
  const discountType: PromoMetadata['discountType'] =
    type === 'percentage' || type === 'absolute' || type === 'fixed' || type === 'fixed_unit' ? type : null;

  return {
    code: promo.code,
    status: 'active',
    name: promo.name || null,
    discountType,
    discountValue: discount?.value ?? null,
    discountScope: discount?.scope ?? null,
    validUntil: promo.validUntil,
    minOrderValue: promo.minOrderValue,
    eligibility: {
      productIds: [],
      categoryIds: [],
      manufacturerIds: [],
      tagIds: [],
      productStreamIds: [],
      unknown: false,
    },
    userDescription: promo.userDescription || null,
    // Only treat it as "shown on the PDP" when a discounted price is actually computed: a
    // priceOnPdp flag without an effectivePromotionPrice renders no Streichpreis, so the
    // subline must not claim the discount is already applied on the page (ORT-3110).
    priceOnPdp: promo.priceOnPdp && promo.effectivePromotionPrice != null,
  };
}

export function pdpPromotionToAppliedState(promo: PdpPromotion): Extract<PromoState, { kind: 'applied' }> {
  return {
    kind: 'applied',
    code: promo.code,
    metadata: pdpPromotionToMetadata(promo),
    eligibleForThisProduct: true,
  };
}
